// Image Performance Monitoring Utility
// Tracks image loading performance and provides optimization insights

class ImagePerformanceMonitor {
  constructor() {
    this.metrics = {
      loadTimes: [],
      cacheHits: 0,
      cacheMisses: 0,
      errors: 0,
      slowImages: []
    };
    
    this.slowImageThreshold = 3000; // 3 seconds
    this.isEnabled = process.env.NODE_ENV === 'development';
  }

  // Track image load start
  trackImageStart(url) {
    if (!this.isEnabled) return;
    
    const startTime = performance.now();
    return {
      url,
      startTime,
      endTime: null,
      loadTime: null
    };
  }

  // Track image load completion
  trackImageEnd(trackingData, fromCache = false) {
    if (!this.isEnabled || !trackingData) return;

    const endTime = performance.now();
    const loadTime = endTime - trackingData.startTime;
    
    trackingData.endTime = endTime;
    trackingData.loadTime = loadTime;
    trackingData.fromCache = fromCache;

    this.metrics.loadTimes.push(loadTime);
    
    if (fromCache) {
      this.metrics.cacheHits++;
    } else {
      this.metrics.cacheMisses++;
    }

    // Track slow images
    if (loadTime > this.slowImageThreshold) {
      this.metrics.slowImages.push({
        url: trackingData.url,
        loadTime: Math.round(loadTime),
        fromCache
      });
      
      console.warn(`Slow image detected: ${trackingData.url} took ${Math.round(loadTime)}ms`);
    }

    // Log performance in development
    if (this.isEnabled) {
      console.log(`Image loaded: ${trackingData.url} in ${Math.round(loadTime)}ms ${fromCache ? '(cached)' : ''}`);
    }
  }

  // Track image error
  trackImageError(url, error) {
    if (!this.isEnabled) return;
    
    this.metrics.errors++;
    console.error(`Image failed to load: ${url}`, error);
  }

  // Get performance statistics
  getStats() {
    const loadTimes = this.metrics.loadTimes;
    const totalImages = loadTimes.length;
    
    if (totalImages === 0) {
      return {
        totalImages: 0,
        averageLoadTime: 0,
        medianLoadTime: 0,
        cacheHitRate: 0,
        errorRate: 0,
        slowImages: this.metrics.slowImages.length
      };
    }

    const sortedLoadTimes = [...loadTimes].sort((a, b) => a - b);
    const averageLoadTime = loadTimes.reduce((sum, time) => sum + time, 0) / totalImages;
    const medianLoadTime = sortedLoadTimes[Math.floor(totalImages / 2)];
    const cacheHitRate = this.metrics.cacheHits / (this.metrics.cacheHits + this.metrics.cacheMisses) * 100;
    const errorRate = this.metrics.errors / totalImages * 100;

    return {
      totalImages,
      averageLoadTime: Math.round(averageLoadTime),
      medianLoadTime: Math.round(medianLoadTime),
      cacheHitRate: Math.round(cacheHitRate * 100) / 100,
      errorRate: Math.round(errorRate * 100) / 100,
      slowImages: this.metrics.slowImages.length,
      slowImageThreshold: this.slowImageThreshold
    };
  }

  // Get slow images report
  getSlowImagesReport() {
    return this.metrics.slowImages.map(img => ({
      url: img.url,
      loadTime: img.loadTime,
      fromCache: img.fromCache,
      recommendation: this.getOptimizationRecommendation(img)
    }));
  }

  // Get optimization recommendations
  getOptimizationRecommendation(slowImage) {
    const recommendations = [];
    
    if (slowImage.loadTime > 5000) {
      recommendations.push('Consider reducing image quality or size');
    }
    
    if (!slowImage.fromCache) {
      recommendations.push('Implement better caching strategy');
    }
    
    if (slowImage.url.includes('pinata.cloud')) {
      recommendations.push('Consider using a CDN or image proxy');
    }
    
    return recommendations;
  }

  // Reset metrics
  reset() {
    this.metrics = {
      loadTimes: [],
      cacheHits: 0,
      cacheMisses: 0,
      errors: 0,
      slowImages: []
    };
  }

  // Export metrics for analysis
  exportMetrics() {
    return {
      stats: this.getStats(),
      slowImages: this.getSlowImagesReport(),
      timestamp: new Date().toISOString()
    };
  }
}

// Create singleton instance
const imagePerformanceMonitor = new ImagePerformanceMonitor();

// Performance optimization suggestions
export const getOptimizationSuggestions = (stats) => {
  const suggestions = [];
  
  if (stats.cacheHitRate < 50) {
    suggestions.push({
      type: 'caching',
      priority: 'high',
      message: 'Low cache hit rate. Consider implementing better image caching.',
      action: 'Review cache strategy and increase cache duration'
    });
  }
  
  if (stats.averageLoadTime > 2000) {
    suggestions.push({
      type: 'performance',
      priority: 'high',
      message: 'High average load time. Consider image optimization.',
      action: 'Implement image compression and CDN'
    });
  }
  
  if (stats.slowImages > 5) {
    suggestions.push({
      type: 'slow_images',
      priority: 'medium',
      message: 'Multiple slow images detected.',
      action: 'Review slow images and optimize them'
    });
  }
  
  if (stats.errorRate > 5) {
    suggestions.push({
      type: 'errors',
      priority: 'high',
      message: 'High error rate for image loading.',
      action: 'Check image URLs and implement better error handling'
    });
  }
  
  return suggestions;
};

// Image loading best practices
export const IMAGE_LOADING_BEST_PRACTICES = {
  // Lazy loading
  lazyLoading: {
    enabled: true,
    rootMargin: '50px',
    threshold: 0.1
  },
  
  // Preloading
  preloading: {
    criticalImages: 6,
    maxConcurrent: 3,
    timeout: 15000
  },
  
  // Caching
  caching: {
    strategy: 'aggressive',
    expiry: 24 * 60 * 60 * 1000, // 24 hours
    maxSize: 50 * 1024 * 1024 // 50MB
  },
  
  // Optimization
  optimization: {
    format: 'webp',
    quality: 80,
    progressive: true,
    stripMetadata: true
  }
};

export default imagePerformanceMonitor;
